import { useParams, useNavigate } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ArrowLeft, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { accountsApi } from '@/api'
import { AccountCard } from '@/components/profile/AccountCard'
import { AccountCardSkeleton } from '@/components/common'
import type { AccountWithStats } from '@olimhub/shared'

export function FollowersPage() {
  const { handle } = useParams<{ handle: string }>()
  const username = handle?.replace(/^@/, '') ?? ''
  const navigate = useNavigate()
  const { t } = useTranslation()

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['followers', username],
    queryFn: ({ pageParam }) =>
      accountsApi.getFollowers(username!, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!username,
  })

  const items = data?.pages.flatMap((p) => p.items as AccountWithStats[]) ?? []
  const isLoading = isFetching && items.length === 0

  return (
    <div style={{ maxWidth: 'var(--feed-max)', margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          type="button"
          aria-label="Go back"
          onClick={() => navigate(-1)}
          style={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            color: 'var(--text-strong)',
            fontSize: 'var(--fs-body)',
          }}
        >
          {t('profile.followers_of', { username })}
        </span>
      </div>

      {/* List */}
      {isLoading ? (
        <>
          <AccountCardSkeleton />
          <AccountCardSkeleton />
          <AccountCardSkeleton />
        </>
      ) : items.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
          <Users size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
          <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
            {t('profile.no_followers')}
          </p>
        </div>
      ) : (
        <>
          {items.map((acc) => (
            <AccountCard key={acc.id} account={acc} />
          ))}

          {hasNextPage && (
            <div style={{ padding: '12px 16px' }}>
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                style={{
                  width: '100%',
                  padding: '12px 0',
                  border: '1.5px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  cursor: isFetchingNextPage ? 'not-allowed' : 'pointer',
                  opacity: isFetchingNextPage ? 0.6 : 1,
                }}
              >
                {isFetchingNextPage ? t('feed.loading_more') : t('feed.load_more')}
              </button>
            </div>
          )}

          {!hasNextPage && items.length > 0 && (
            <p
              style={{
                textAlign: 'center',
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-faint)',
                fontFamily: 'var(--font-sans)',
                padding: '16px 0 8px',
                margin: 0,
              }}
            >
              {t('feed.end')}
            </p>
          )}
        </>
      )}
    </div>
  )
}
